/**
 * Bridges the Flutter WebView wrapper's native device info into every
 * outgoing axios request as X-Device-* headers. This is what makes the
 * mobile/web split structural rather than guessed: `flutter_inappwebview`
 * (the WebView engine tarekmath_mobile uses) auto-injects
 * `window.flutter_inappwebview` into every page it loads — a plain browser
 * never has it, so `deviceHeaders` stays null and no X-Device-* header is
 * ever attached outside the wrapped app.
 */
let deviceHeaders = null;
let initPromise = null;

const HEADER_MAP = {
  device_uuid: 'X-Device-Uuid',
  android_id: 'X-Device-Android-Id',
  brand: 'X-Device-Brand',
  manufacturer: 'X-Device-Manufacturer',
  model: 'X-Device-Model',
  device_name: 'X-Device-Name',
  android_version: 'X-Device-Android-Version',
  sdk_version: 'X-Device-Sdk-Version',
  package_name: 'X-Device-Package-Name',
  platform: 'X-Device-Platform',
  app_version: 'X-Device-App-Version',
  app_version_code: 'X-Device-App-Version-Code',
};

function isInsideWrapper() {
  return typeof window !== 'undefined' && typeof window.flutter_inappwebview !== 'undefined';
}

async function init() {
  if (!isInsideWrapper()) return;
  if (initPromise) return initPromise;

  initPromise = window.flutter_inappwebview
    .callHandler('getDeviceInfo')
    .then((info) => {
      deviceHeaders = info && Object.keys(info).length ? info : null;
    })
    .catch((e) => {
      // Device info couldn't be collected — fall back to plain, unbound
      // requests (no X-Device-* headers). The site must keep working.
      // eslint-disable-next-line no-console
      console.warn('[deviceInfo] getDeviceInfo bridge failed, continuing without device headers', e);
      deviceHeaders = null;
    });

  return initPromise;
}

/**
 * Registers an axios request interceptor that attaches the cached device
 * headers plus a freshly-signed X-Timestamp/X-Nonce/X-Signature/X-Android-Id
 * set (phase 2). Signing can't be precomputed once like the rest of the
 * device info — timestamp/nonce must be fresh per request — and it can't
 * happen here in JS either: this bundle is served to any browser, not just
 * the wrapped app, so the HMAC secret must never leave native code. Each
 * outgoing request therefore makes one extra native bridge round-trip
 * (signRequest) — the interceptor is async to accommodate that.
 */
/**
 * CHALLENGE FLOW (server timestamp + nonce → device clock is never signed):
 *  1) fetch a short-lived, device-bound challenge from the backend using a RAW
 *     client (no interceptors) so this bootstrap call never recurses into signing;
 *  2) hand the server's {challenge_id, timestamp, nonce} to native signChallenge,
 *     which HMAC-signs them (secret stays native) and returns the signed headers.
 * Returns the header map, or null so the caller can fall back to the legacy flow.
 */
async function signViaChallenge(rawClient) {
  try {
    const deviceUuid = deviceHeaders.device_uuid;
    const androidId = deviceHeaders.android_id || '';
    const { data } = await rawClient.post('/mobile/auth/challenge',
      { device_uuid: deviceUuid, android_id: androidId },
      { headers: { 'X-Device-Uuid': deviceUuid, 'X-Android-Id': androidId } });

    const ch = data && data.challenge;
    if (!ch || !ch.challenge_id) return null;

    const signed = await window.flutter_inappwebview.callHandler(
      'signChallenge', ch.challenge_id, String(ch.timestamp), ch.nonce,
    );
    return (signed && signed['X-Signature']) ? signed : null;
  } catch (e) {
    // Network/native error → let the caller fall back to legacy signing. A signing
    // hiccup must never take the UI offline (backend still validates whatever arrives).
    // eslint-disable-next-line no-console
    console.warn('[deviceInfo] challenge signing failed, will try legacy signing', e);
    return null;
  }
}

/** LEGACY FLOW (client timestamp + nonce) — unchanged, used only as a fallback. */
async function signViaLegacy() {
  try {
    const signed = await window.flutter_inappwebview.callHandler('signRequest');
    return (signed && signed['X-Signature']) ? signed : null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[deviceInfo] legacy signRequest bridge failed, sending request unsigned', e);
    return null;
  }
}

function attach(axios) {
  // Clean client for the challenge bootstrap: it MUST NOT carry this interceptor,
  // otherwise fetching a challenge would try to fetch a challenge (infinite loop).
  const rawClient = axios.create({ baseURL: axios.defaults.baseURL });

  axios.interceptors.request.use(async (config) => {
    // ROOT-CAUSE FIX (first-registration "invalid signature"): the native
    // device bridge initialises ASYNCHRONOUSLY (Android keystore + one-time
    // UUID generation on a fresh install, which can take a noticeable moment
    // on cold start). Previously this interceptor read `deviceHeaders`
    // synchronously and, if init() had not resolved yet, short-circuited via
    // `if (!deviceHeaders) return config;` — sending the very FIRST request
    // (e.g. the login POST) UNSIGNED and without any X-Device-* headers. With
    // MOBILE_HMAC_STRICT=true the backend then rejects it as "Invalid or
    // missing request signature." Awaiting the memoised init() here makes
    // every request wait for the bridge to be ready, so the first signed
    // request is never sent before the device identity exists. Outside the
    // wrapper (plain browser) init() resolves immediately to a no-op.
    if (isInsideWrapper()) {
      // Wait for the bridge, but never freeze the app if the native side is
      // pathologically stuck — 10s is far beyond a normal cold-start init.
      await Promise.race([
        init(),
        new Promise((resolve) => { setTimeout(resolve, 10000); }),
      ]);
    }

    if (!deviceHeaders) {
      return config;
    }

    config.headers = config.headers || {};
    for (const [field, header] of Object.entries(HEADER_MAP)) {
      if (deviceHeaders[field]) {
        config.headers[header] = deviceHeaders[field];
      }
    }

    if (isInsideWrapper()) {
      // Prefer the CHALLENGE flow (clock-independent). Fall back to the LEGACY flow
      // only if the challenge round-trip/native handler is unavailable (older app
      // build without signChallenge, or a transient error). Both are validated by the
      // backend; an unsigned request is never silently accepted server-side.
      const signed = (await signViaChallenge(rawClient)) || (await signViaLegacy());
      if (signed) {
        // X-Challenge-Id is present ONLY in the challenge flow; its presence is what
        // tells the backend which verifier to use.
        if (signed['X-Challenge-Id']) config.headers['X-Challenge-Id'] = signed['X-Challenge-Id'];
        if (signed['X-Android-Id']) config.headers['X-Android-Id'] = signed['X-Android-Id'];
        if (signed['X-Timestamp']) config.headers['X-Timestamp'] = signed['X-Timestamp'];
        if (signed['X-Nonce']) config.headers['X-Nonce'] = signed['X-Nonce'];
        if (signed['X-Signature']) config.headers['X-Signature'] = signed['X-Signature'];
      }
      // Log header NAMES + presence only (safe metadata) — never values/signature/secret.
      const present = ['X-Challenge-Id', 'X-Device-Uuid', 'X-Android-Id', 'X-Timestamp', 'X-Nonce', 'X-Signature']
        .filter((h) => config.headers[h]);
      // eslint-disable-next-line no-console
      console.log('[DEVICE] signed request [' + present.join(', ') + '] ->', config.url);
    }

    return config;
  });
}

/** Called from the WebView's onDeviceSuspended bridge callback so the native app can show its own suspension screen too. */
function notifySuspended() {
  if (isInsideWrapper() && window.flutter_inappwebview.callHandler) {
    window.flutter_inappwebview.callHandler('onDeviceSuspended');
  }
}

export default { init, attach, notifySuspended, isInsideWrapper };
